import Actions from 'flow/actions';
import { State } from 'flow/types/State';
import { Video2 } from 'flow/types/Video2';
import { addVideoLogEntry } from 'flow/utils/browser-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { joinMeetingMuted } from 'flow/actions/meeting-actions';
import { selectNameInApp } from 'flow/selectors/selectors';
import Video2Resource from 'flow/api/videoconference';
import { Callback } from 'flow/types/HomeClient';
import { AppDispatch } from 'flow/types/Redux';

export const video2IncomingCall = (
    payload: Omit<Video2, 'direction' | 'status'>,
): { type: string; payload: Video2 } => ({
    type: Actions.VIDEO2_INCOMING_CALL,
    payload: { ...payload, direction: 'incoming' as const, status: null },
});

export const video2OutgoingCall = (
    payload: Omit<Video2, 'direction' | 'status'>,
): { type: string; payload: Video2 } => ({
    type: Actions.VIDEO2_OUTGOING_CALL,
    payload: { ...payload, direction: 'outgoing' as const, status: null },
});

export const video2ClearState = () => ({
    type: Actions.VIDEO2_CLEAR_CALL_STATE,
});

export const video2CallDeclined = (payload: {
    extensionKey: ExtensionKey;
    meetingCode?: string;
}) => ({
    type: Actions.VIDEO2_CALL_DECLINED,
    payload,
});

export const video2CallAccepted = (payload: {
    extensionKey: ExtensionKey;
    meetingCode?: string;
}) => ({
    type: Actions.VIDEO2_CALL_ACCEPTED,
    payload,
});

export const video2CallCanceled = (payload: {
    extensionKey: ExtensionKey;
    meetingCode?: string;
}) => ({
    type: Actions.VIDEO2_CALL_CANCELED,
    payload,
});

export const video2CallHandledOnOtherDevice = (payload: {
    extensionKey: ExtensionKey;
    meetingCode?: string;
}) => ({
    type: Actions.VIDEO2_CALL_HANDLED,
    payload,
});

export const clearVideo2CallState = () => (dispatch: AppDispatch) => {
    dispatch(video2ClearState());
};

export const handleVideo2OutgoingCall =
    (extensionKey: ExtensionKey, meetingCode: string) =>
    async (dispatch: AppDispatch) => {
        addVideoLogEntry(
            `Starting Video2 call to ${extensionKey} with meetingCode ${meetingCode}`,
        );
        dispatch(
            video2OutgoingCall({
                extensionKey,
                meetingCode,
            }),
        );
    };

export const handleVideo2IncomingCall =
    (videoCallDetails: Video2) => async (dispatch: AppDispatch) => {
        if (videoCallDetails.extensionKey) {
            addVideoLogEntry(
                `Incoming Video2 call from ${videoCallDetails.extensionKey} with meetingCode ${videoCallDetails.meetingCode}`,
            );
            dispatch(video2IncomingCall(videoCallDetails));
        }
    };

export const handleVideo2CallDeclined =
    (payload: { extensionKey: ExtensionKey; meetingCode?: string }) =>
    async (dispatch: AppDispatch) => {
        addVideoLogEntry(`Video2 call declined by ${payload.extensionKey}`);
        dispatch(video2CallDeclined(payload));
    };

export const handleVideo2CallAccepted =
    (payload: { extensionKey: ExtensionKey }) =>
    async (dispatch: AppDispatch, getState: () => State) => {
        addVideoLogEntry(`Video2 call accepted by ${payload.extensionKey}`);

        dispatch(video2CallAccepted(payload));

        const state = getState();
        const meetingCode = state.video2?.get('meetingCode');
        // This handler also runs on the caller's own client, via the
        // 'video_call_answered' push fired once the other side answers — the
        // caller's meetingCode is already set by handleVideo2OutgoingCall, so
        // without this gate the caller would join (and mute) themselves too.
        // AC-1913 only wants the acceptor muted on join.
        const direction = state.video2?.get('direction');

        if (meetingCode && direction === 'incoming') {
            const nameInApp = selectNameInApp(state);

            await dispatch(joinMeetingMuted(nameInApp, meetingCode));

            addVideoLogEntry(
                `Joined Video2 call with meetingCode ${meetingCode}`,
            );
        }
    };

export const handleVideo2CallCanceled =
    (payload: { extensionKey: ExtensionKey; meetingCode?: string }) =>
    async (dispatch: AppDispatch) => {
        addVideoLogEntry(`Video2 call canceled by ${payload.extensionKey}`);
        dispatch(video2CallCanceled(payload));
    };

export const handleVideo2CallHandledOnOtherDevice =
    (payload: { extensionKey: ExtensionKey; meetingCode?: string }) =>
    async (dispatch: AppDispatch, getState: () => State) => {
        const currentVideoCallState = getState().video2;
        const currentStatus = currentVideoCallState?.get('status');
        const currentDirection = currentVideoCallState?.get('direction');

        if (currentStatus === 'accepted' || currentStatus === 'declined') {
            addVideoLogEntry(
                `Ignoring 'handled on other device' - call already ${currentStatus} on this device`,
            );
            return;
        }

        if (
            currentVideoCallState &&
            currentDirection === 'incoming' &&
            currentStatus === null
        ) {
            addVideoLogEntry(
                `Video2 call handled on other device for ${payload.extensionKey}`,
            );
            dispatch(video2CallHandledOnOtherDevice(payload));
        } else {
            addVideoLogEntry(
                `Ignoring 'handled on other device' - no active pending incoming call (status: ${currentStatus}, direction: ${currentDirection})`,
            );
        }
    };

export const fetchVideo2Config = async () => {
    return new Promise((resolve, reject) => {
        Video2Resource.getVideoConferenceConfig({
            onResult(json) {
                resolve(json);
            },
            onError: (error: Error) =>
                reject(
                    new Error(
                        error?.message ||
                            'Failed to get video conference config',
                    ),
                ),
        } as Callback);
    });
};

export const fetchVideo2MeetingCode = async (meetingEntity?: string) => {
    if (meetingEntity) {
        return new Promise((resolve, reject) => {
            Video2Resource.getMeetingCodeForEntity(
                {
                    onResult(json) {
                        resolve(json);
                    },
                    onError: (error: Error) =>
                        reject(
                            new Error(
                                error?.message ||
                                    'Failed to get meeting code for entity',
                            ),
                        ),
                } as Callback,
                meetingEntity,
            );
        });
    } else {
        return new Promise((resolve, reject) => {
            Video2Resource.getMeetingCode({
                onResult(json) {
                    resolve(json);
                },
                onError: (error: Error) =>
                    reject(
                        new Error(
                            error?.message || 'Failed to get meeting code',
                        ),
                    ),
            } as Callback);
        });
    }
};

export const Video2Call = async (extensionKey: ExtensionKey) => {
    return new Promise((resolve, reject) => {
        Video2Resource.call(
            {
                onResult: (result: string) => resolve(result),
                onError: (error: Error) =>
                    reject(
                        new Error(
                            error?.message || 'Failed to make video call',
                        ),
                    ),
            } as Callback,
            extensionKey,
        );
    });
};

export const notifyVideoCall =
    (extensionKey: ExtensionKey, meetingCode: string) => () =>
        new Promise((resolve, reject) => {
            Video2Resource.notify(
                {
                    onResult: (result: string) => resolve(result),
                    onError: (error: Error) =>
                        reject(
                            new Error(
                                error?.message || 'Failed to notify video call',
                            ),
                        ),
                } as Callback,
                extensionKey,
                meetingCode,
            );
        });

export const declineVideoCall = async (extensionKey: ExtensionKey) => {
    return new Promise((resolve, reject) => {
        Video2Resource.declineVideoCall(
            {
                onResult: (result: string) => resolve(result),
                onError: (error: Error) =>
                    reject(
                        new Error(
                            error?.message || 'Failed to decline video call',
                        ),
                    ),
            },
            extensionKey,
        );
    });
};

export const answerVideoCall = async (extensionKey: ExtensionKey) => {
    return new Promise((resolve, reject) => {
        Video2Resource.answerVideoCall(
            {
                onResult: (result: string) => resolve(result),
                onError: (error: Error) =>
                    reject(
                        new Error(
                            error?.message || 'Failed to answer video call',
                        ),
                    ),
            },
            extensionKey,
        );
    });
};

export const cancelVideoCall = async (extensionKey: ExtensionKey) => {
    return new Promise((resolve, reject) => {
        Video2Resource.cancelVideoCall(
            {
                onResult: (result: string) => resolve(result),
                onError: (error: Error) =>
                    reject(
                        new Error(
                            error?.message || 'Failed to cancel video call',
                        ),
                    ),
            },
            extensionKey,
        );
    });
};
